import { Fragment, useEffect, useState } from "react";
import { CaretDown, CaretUp, Sparkle, Gavel, Scroll, Check, Thermometer, Compass } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const SEV = {
  critical: { color: "var(--c-red)", label: "Critical" },
  major: { color: "var(--c-amber)", label: "Major" },
  minor: { color: "var(--c-accent)", label: "Minor" },
  advisory: { color: "var(--c-muted)", label: "Advisory" },
};

export default function IssuesTable({ issues, canWaive, onWaive, onApplyFix, onAskAi, aiLoading, acting, projectId, focusCheck, onGuide }) {
  const [expanded, setExpanded] = useState(null);
  const [waiveFor, setWaiveFor] = useState(null);
  const [justification, setJustification] = useState("");
  const [cooling, setCooling] = useState(null);
  const [coolingBusy, setCoolingBusy] = useState(false);

  useEffect(() => {
    if (!focusCheck) return;
    const hit = issues.find((i) => i.check_id === focusCheck);
    if (!hit) return;
    setExpanded(hit.id);
    const el = document.querySelector(`[data-testid="issue-row-${hit.check_id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusCheck, issues]);

  const loadCooling = async () => {
    setCoolingBusy(true);
    try {
      const { data } = await api.post(`/validation/${projectId}/cooling-options`);
      setCooling(data);
      if (!data.options.length) toast.info("No cooling upgrade improved the design");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setCoolingBusy(false); }
  };

  if (!issues.length) {
    return (
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-8 text-center" data-testid="issues-empty">
        <Check size={24} weight="bold" className="mx-auto text-[var(--c-green)]" />
        <div className="text-sm text-[var(--c-text)] mt-2">No issues — all checks pass</div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-hidden" data-testid="issues-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--c-border)]">
            {["Severity", "ID", "Domain", "Description", "Calculated", "Required", "Status", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2.5 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {issues.map((i) => {
            const sev = SEV[i.severity];
            const isOpen = expanded === i.id;
            return (
              <Fragment key={i.id}>
                <tr className="border-b border-[var(--c-border)]/50 hover:bg-[var(--c-border)]/20 cursor-pointer"
                  data-testid={`issue-row-${i.check_id}`}
                  onClick={() => setExpanded(isOpen ? null : i.id)}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono" style={{ color: sev.color }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sev.color }} />{sev.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[var(--c-muted)]">{i.check_id}</td>
                  <td className="px-3 py-2.5 text-[var(--c-muted)] capitalize">{i.domain.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2.5 text-[var(--c-text)]">
                    {i.title}
                    {i.client_rule && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 text-[var(--c-accent)]"
                        data-testid={`client-rule-badge-${i.check_id}`}>
                        <Scroll size={9} /> Client Rule Applied
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[var(--c-amber)]">{i.calculated}</td>
                  <td className="px-3 py-2.5 font-mono text-[var(--c-muted)]">{i.required}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[9px] font-mono tracking-wider ${i.status === "waived" ? "text-[var(--c-muted)]" : "text-[var(--c-amber)]"}`}
                      data-testid={`issue-status-${i.check_id}`}>
                      {i.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--c-muted)]">{isOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}</td>
                </tr>
                {isOpen && (
                  <tr className="border-b border-[var(--c-border)]/50 bg-[var(--c-bg)]/60" data-testid={`issue-detail-${i.check_id}`}>
                    <td colSpan={8} className="px-4 py-4">
                      <div className="text-[11px] text-[var(--c-muted)] mb-3">{i.description}</div>
                      {i.client_rule && (
                        <div className="text-[11px] text-[var(--c-accent)] mb-3 flex items-center gap-1.5">
                          <Scroll size={12} /> Stricter limit enforced by client rule: <span className="font-medium">{i.client_rule}</span>
                        </div>
                      )}
                      {i.status === "waived" && (
                        <div className="text-[11px] text-[var(--c-muted)] mb-3 border border-[var(--c-border)] rounded-sm px-3 py-2" data-testid={`waived-note-${i.check_id}`}>
                          Waived by <span className="text-[var(--c-text)]">{i.waived_by}</span> on {String(i.waived_at || "").slice(0, 10)} — {i.justification}
                        </div>
                      )}
                      {i.suggestions.length > 0 && i.status !== "waived" && (
                        <div className="space-y-2 mb-3">
                          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Suggested fixes</div>
                          {i.suggestions.map((s, j) => (
                            <div key={j} className="flex items-center gap-3 flex-wrap bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm px-3 py-2"
                              data-testid={`suggestion-${i.check_id}-${j}`}>
                              <span className="text-xs text-[var(--c-text)]">{s.label}</span>
                              {s.requires_approval && (
                                <span data-testid={`approval-badge-${i.check_id}-${j}`}
                                  className="text-[9px] font-mono tracking-wider uppercase px-1.5 py-0.5 rounded-sm border border-[var(--c-amber)]/50 bg-[var(--c-amber)]/10 text-[var(--c-amber)]">
                                  changes a guarantee · client approval
                                </span>
                              )}
                              <span className="text-[10px] font-mono text-[var(--c-muted)]">{s.impact}</span>
                              {((s.inputs_patch && Object.keys(s.inputs_patch).length > 0) || (s.specs_patch && Object.keys(s.specs_patch).length > 0)) && (
                                <Button size="sm" data-testid={`apply-fix-${i.check_id}-${j}`} disabled={acting}
                                  onClick={(e) => { e.stopPropagation(); onApplyFix(i, s); }}
                                  className="ml-auto bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
                                  Apply Fix
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {i.domain === "thermal" && i.status !== "waived" && projectId && (
                        <div className="mb-3" data-testid={`cooling-solver-${i.check_id}`}>
                          <Button size="sm" variant="outline" data-testid={`cooling-options-${i.check_id}`}
                            disabled={coolingBusy} onClick={loadCooling}
                            className="border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-7 text-[10px]">
                            <Thermometer size={11} className="mr-1" />
                            {coolingBusy ? "Modelling…" : cooling ? "Re-model cooling options" : "Cooling options (modelled)"}
                          </Button>
                          {cooling && (
                            <div className="mt-2 border border-[var(--c-border)] rounded-lg overflow-hidden">
                              <div className="px-3 py-2 bg-[var(--c-surface)] border-b border-[var(--c-border)] font-mono text-[10px] text-[var(--c-muted)] flex flex-wrap gap-x-4 gap-y-1">
                                <span>Baseline: {cooling.baseline.cooling_type} · {cooling.baseline.radiator_panels} panels</span>
                                <span>Top oil {cooling.baseline.top_oil_rise_c} K</span>
                                <span>Hot spot {cooling.baseline.hot_spot_abs_c} °C</span>
                                <span>Health {cooling.baseline.health_score}</span>
                              </div>
                              {cooling.options.map((o, k) => (
                                <div key={k} className="flex items-start gap-3 flex-wrap px-3 py-2.5 border-b border-[var(--c-border)]/60 last:border-0 bg-[var(--c-bg)]/40"
                                  data-testid={`cooling-option-${k}`}>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs text-[var(--c-text)]">{o.label}</div>
                                    <div className="text-[10px] text-[var(--c-muted)] mt-0.5">{o.note}</div>
                                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px]">
                                      <span className={o.top_oil_delta_k < 0 ? "text-[var(--c-green)]" : "text-[var(--c-muted)]"}>
                                        Top oil {o.top_oil_rise_c} K ({o.top_oil_delta_k > 0 ? "+" : ""}{o.top_oil_delta_k})
                                      </span>
                                      <span className={o.hot_spot_delta_k < 0 ? "text-[var(--c-green)]" : "text-[var(--c-muted)]"}>
                                        Hot spot {o.hot_spot_abs_c} °C ({o.hot_spot_delta_k > 0 ? "+" : ""}{o.hot_spot_delta_k})
                                      </span>
                                      <span className="text-[var(--c-muted)]">Margin {o.onaf_overload_margin_pct}%</span>
                                      <span className={o.health_delta > 0 ? "text-[var(--c-green)]" : "text-[var(--c-muted)]"}>
                                        Health {o.health_score} ({o.health_delta > 0 ? "+" : ""}{o.health_delta})
                                      </span>
                                      {o.weight_delta_kg != null && <span className="text-[var(--c-muted)]">Weight {o.weight_delta_kg > 0 ? "+" : ""}{o.weight_delta_kg} kg</span>}
                                    </div>
                                    {o.clears.length > 0 && (
                                      <div className="text-[10px] text-[var(--c-green)] mt-1 font-mono">Clears: {o.clears.join(", ")}</div>
                                    )}
                                    {o.introduces.length > 0 && (
                                      <div className="text-[10px] text-[var(--c-amber)] mt-0.5 font-mono">Introduces: {o.introduces.join(", ")}</div>
                                    )}
                                  </div>
                                  <Button size="sm" data-testid={`cooling-apply-${k}`} disabled={acting}
                                    onClick={(e) => { e.stopPropagation(); onApplyFix(i, { specs_patch: o.specs_patch }); setCooling(null); }}
                                    className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
                                    Apply
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {i.ai_suggestion && (
                        <div className="bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 rounded-sm px-3 py-2.5 mb-3" data-testid={`ai-suggestion-${i.check_id}`}>
                          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-1 flex items-center gap-1">
                            <Sparkle size={10} /> AI engineering suggestion
                          </div>
                          <div className="text-xs text-[var(--c-text)] whitespace-pre-wrap">{i.ai_suggestion}</div>
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        {i.status !== "waived" && onGuide && (
                          <Button size="sm" variant="outline" data-testid={`how-to-fix-${i.check_id}`}
                            onClick={() => onGuide(i)}
                            className="border-[var(--c-green)]/50 text-[var(--c-green)] hover:bg-[var(--c-green)]/10 rounded-sm h-7 text-[10px]">
                            <Compass size={11} className="mr-1" /> How to fix this
                          </Button>
                        )}
                        {i.status !== "waived" && (
                          <Button size="sm" variant="outline" data-testid={`ask-ai-${i.check_id}`}
                            disabled={aiLoading === i.id} onClick={() => onAskAi(i)}
                            className="border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-7 text-[10px]">
                            <Sparkle size={11} className="mr-1" /> {aiLoading === i.id ? "Asking Cognara AI…" : "Ask AI"}
                          </Button>
                        )}
                        {i.status !== "waived" && canWaive && (
                          waiveFor === i.id ? (
                            <div className="flex items-center gap-2 flex-1 min-w-72">
                              <input autoFocus data-testid={`waive-justification-${i.check_id}`} value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                placeholder="Justification (min 5 chars)…"
                                className="flex-1 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm h-7 px-2 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-amber)]" />
                              <Button size="sm" data-testid={`waive-confirm-${i.check_id}`} disabled={justification.trim().length < 5 || acting}
                                onClick={() => { onWaive(i, justification.trim()); setWaiveFor(null); setJustification(""); }}
                                className="bg-[var(--c-amber)] hover:bg-[var(--c-amber)]/85 text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                                Confirm Waive
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" data-testid={`waive-btn-${i.check_id}`}
                              onClick={() => { setWaiveFor(i.id); setJustification(""); }}
                              className="border-[var(--c-amber)]/50 text-[var(--c-amber)] hover:bg-[var(--c-amber)]/10 rounded-sm h-7 text-[10px]">
                              <Gavel size={11} className="mr-1" /> Waive
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
